/**
 * trendIntegrity — Grimes-style trend-integrity classifier.
 *
 * Per Adam Grimes's writing on multi-timeframe analysis, the right question
 * for HTF context is not "what's the trend?" but "is the trend intact,
 * breaking, or broken?" — because by the time most trend indicators flash
 * a fresh signal, the trend has already shifted.
 *
 * The four states:
 *   intact   — making higher highs + higher lows (up) or lower lows + lower
 *              highs (down) consistently over recent swings.
 *   breaking — the most recent swing pivot violates the prior sequence
 *              within a small tolerance (< 0.3 × ATR by default).
 *   broken   — most recent pivot violates by more than tolerance.
 *   no-trend — no clear pivot sequence — chop or insufficient pivots.
 *
 * Implementation reuses `swingDetect.detectSwings` for pivot extraction and
 * `entryDiscipline.computeATRAtIndex` for the violation tolerance.
 *
 * Pure function.
 */

import { detectSwings } from './swingDetect.js';
import { computeATRAtIndex } from './entryDiscipline.js';

export const DEFAULT_OPTS = Object.freeze({
  pivotBars: 3,               // strictness of swing detection
  minPivotsForTrend: 4,       // need 4+ pivots to assess trend integrity
  breakingTolerance: 0.3,     // < 0.3 × ATR pivot violation = "breaking"
});

/**
 * Determine trend integrity at the end of `bars`.
 *
 * @param {Array<{time:number, high:number, low:number, close:number}>} bars
 * @param {Object} [opts]
 * @returns {'intact' | 'breaking' | 'broken' | 'no-trend'}
 */
export const detectTrendIntegrityAt = (bars, opts = {}) => {
  if (!Array.isArray(bars) || bars.length < 10) return 'no-trend';

  const pivotBars = Math.max(1, Math.floor(Number(opts.pivotBars ?? DEFAULT_OPTS.pivotBars)));
  const minPivots = Math.max(3, Math.floor(Number(opts.minPivotsForTrend ?? DEFAULT_OPTS.minPivotsForTrend)));
  const tolerance = Number(opts.breakingTolerance ?? DEFAULT_OPTS.breakingTolerance);

  const swings = detectSwings(bars, { leftBars: pivotBars, rightBars: pivotBars });
  if (!Array.isArray(swings) || swings.length < minPivots) return 'no-trend';

  // Take the last `minPivots` pivots and check if they're a clean
  // HH/HL or LL/LH sequence.
  const recent = swings.slice(-minPivots);

  // Separate into highs and lows to examine each sequence's monotonicity.
  const highs = recent.filter(s => s.type === 'high').map(s => s.price);
  const lows = recent.filter(s => s.type === 'low').map(s => s.price);
  if (highs.length < 2 || lows.length < 2) return 'no-trend';

  const allRising = (arr) => arr.every((v, i) => i === 0 || v > arr[i - 1]);
  const allFalling = (arr) => arr.every((v, i) => i === 0 || v < arr[i - 1]);

  if (allRising(highs) && allRising(lows)) return 'intact';
  if (allFalling(highs) && allFalling(lows)) return 'intact';

  // Not clean — measure the violation severity by the most recent pivot
  // that broke its sequence. Compare to ATR scale.
  const atr = computeATRAtIndex(bars, bars.length - 1, 14);
  if (!Number.isFinite(atr) || atr <= 0) return 'no-trend';

  // Find the largest violation in highs or lows.
  let maxViolation = 0;
  const examineSeq = (arr, direction /* 'up' | 'down' */) => {
    for (let i = 1; i < arr.length; i++) {
      const violated = direction === 'up' ? arr[i] <= arr[i - 1] : arr[i] >= arr[i - 1];
      if (violated) {
        maxViolation = Math.max(maxViolation, Math.abs(arr[i] - arr[i - 1]));
      }
    }
  };

  // Determine the overall recent direction from the first vs last pivot of each kind.
  const upHighs = highs[highs.length - 1] > highs[0];
  const upLows = lows[lows.length - 1] > lows[0];
  // If both highs and lows trended up overall, it's an uptrend with violations.
  // Else if both trended down, downtrend with violations. Else: chop.
  if (upHighs && upLows) {
    examineSeq(highs, 'up');
    examineSeq(lows, 'up');
  } else if (!upHighs && !upLows) {
    examineSeq(highs, 'down');
    examineSeq(lows, 'down');
  } else {
    return 'no-trend';
  }

  return maxViolation / atr < tolerance ? 'breaking' : 'broken';
};
